/**
 * Tauri Desktop Commands
 * Type-safe wrappers for Tauri backend commands
 */

import { invoke } from '@tauri-apps/api/tauri';
import type { LicenseInfo } from '@/services/licenseService';
import type { SyncQueueItem, SyncStats } from '@/services/syncService';

// Check if running in Tauri
export const isTauri = () => {
  return typeof window !== 'undefined' && '__TAURI__' in window;
};

// ============================================================================
// License Commands
// ============================================================================

export async function activateLicense(licenseKey: string): Promise<LicenseInfo> {
  return invoke<LicenseInfo>('activate_license', { licenseKey });
}

export async function verifyLicense(online: boolean = true): Promise<LicenseInfo> {
  return invoke<LicenseInfo>('verify_license', { online });
}

export async function deactivateLicense(): Promise<void> {
  return invoke('deactivate_license');
}

export async function getLicenseInfo(): Promise<LicenseInfo | null> {
  return invoke<LicenseInfo | null>('get_license_info');
}

// ============================================================================
// Fingerprint Commands
// ============================================================================

export interface DeviceInfo {
  fingerprint: string;
  device_name: string;
  os_type: string;
  os_version: string;
  hostname: string;
  cpu_cores: number;
  total_memory: number;
}

export async function getDeviceFingerprint(): Promise<string> {
  return invoke<string>('get_device_fingerprint');
}

export async function getDeviceInfo(): Promise<DeviceInfo> {
  return invoke<DeviceInfo>('get_device_info');
}

// ============================================================================
// Sync Commands
// ============================================================================

export type EntityType = 'invoice' | 'product' | 'client' | 'transaction' | 'payment';
export type SyncOperation = 'create' | 'update' | 'delete';

export async function queueChange(
  companyId: string,
  deviceFingerprint: string,
  entityType: EntityType,
  entityId: string,
  operation: SyncOperation,
  data: Record<string, any>
): Promise<string> {
  return invoke<string>('queue_change', {
    companyId,
    deviceFingerprint,
    entityType,
    entityId,
    operation,
    data,
  });
}

export async function processSyncQueue(
  companyId: string
): Promise<{ processed: number; conflicts: number; errors: number }> {
  const [processed, conflicts, errors] = await invoke<[number, number, number]>(
    'process_sync_queue',
    { companyId }
  );
  return { processed, conflicts, errors };
}

export async function getSyncStats(companyId: string): Promise<SyncStats> {
  return invoke<SyncStats>('get_sync_stats', { companyId });
}

export async function getPendingChanges(companyId: string): Promise<SyncQueueItem[]> {
  return invoke<SyncQueueItem[]>('get_pending_changes', { companyId });
}

export async function getConflicts(companyId: string): Promise<SyncQueueItem[]> {
  return invoke<SyncQueueItem[]>('get_conflicts', { companyId });
}

export async function resolveConflict(
  itemId: string,
  resolution: 'server' | 'client'
): Promise<void> {
  return invoke('resolve_conflict', { itemId, resolution });
}

// ============================================================================
// Database Commands
// ============================================================================

export async function initDatabase(): Promise<void> {
  return invoke('init_database');
}

export async function resetDatabase(): Promise<void> {
  return invoke('reset_database');
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Execute a function only if running in Tauri desktop
 */
export async function executeIfTauri<T>(
  fn: () => Promise<T>,
  fallback?: T
): Promise<T | undefined> {
  if (isTauri()) {
    return fn();
  }
  return fallback;
}

/**
 * Get platform-specific data path
 */
export async function getAppDataPath(): Promise<string | undefined> {
  if (!isTauri()) return undefined;

  const { appDataDir } = await import('@tauri-apps/api/path');
  return appDataDir();
}

/**
 * Check if device is online
 */
export function isOnline(): boolean {
  return navigator.onLine;
}

/**
 * Listen for online/offline events
 */
export function onNetworkChange(callback: (online: boolean) => void): () => void {
  const handleOnline = () => callback(true);
  const handleOffline = () => callback(false);

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
}
